/* eslint-disable jsx-a11y/anchor-is-valid */
import classNames from "classnames";
import { Avatar, Sidebar, useThemeMode } from "flowbite-react";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiCog,
  HiMenuAlt1,
  HiOutlineBell,
  HiOutlineBriefcase,
  HiOutlineViewBoards,
  HiUsers,
} from "react-icons/hi";
import { HiMoon, HiSun } from "react-icons/hi2";
import { useDispatch, useSelector } from 'react-redux'
import { useSideNavContext } from "../../context/SideNavContext";
import { isSmallScreen } from "../../helpers/screens";
import { useJwt } from "../../helpers/common";
import { OrganizationSwitcher, useClerk, useOrganizationList, WithUser } from '@clerk/clerk-react'
import { FloatingDropdown } from "../common/FloatingDropdown";
import { Link, useLocation, useNavigate } from "react-router-dom";
import GlobalChatBox from "../GlobalChatBox/GlobalChatBox";
import Portal from "../Portal";
import { ENTITY } from "../../redux/slices/timeline";
import { resetResourcePageState } from "../../redux/slices/resources";
import { useUnallocatedTasksWarning } from "../../hooks/useUnallocatedTasksWarning";
import UnallocatedTasksWarningModal from "../Modals/UnallocatedTasksWarningModal";
import { OnboardingStepKeyEnum, PMTOOLS_PROVIDER_ORDER, useOnboardingStep } from "../Onboarding";
import { SideNavGuide } from "../../features/OnboardingGuide/SidenavGuide/SideNavGuide";
import { ORG_HIDE_STRATEGY_MAPS, ComponentPage } from "../../constants/constants";
import SavedViews from "../../features/SavedViews/SavedViews";
import { clearActiveView } from "../../redux/slices/views";
import { useResourcesService } from "../../services";

const sidebarTooltipStyles = `
  <style>
    .sidebarinner [data-tooltip-target] + div[role="tooltip"] {
      background: linear-gradient(135deg, #374151, #4b5563) !important;
      border: 1px solid #6b7280 !important;
      border-radius: 8px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      padding: 8px 12px !important;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
    }
    .sidebarinner [data-tooltip-target] + div[role="tooltip"] .tooltip-arrow {
      background: #374151 !important;
      border-color: #6b7280 !important;
    }
    /* Modern dropdown animations */
    [data-floating-ui-portal] {
      animation: dropdownSlideIn 0.2s ease-out;
    }
    @keyframes dropdownSlideIn {
      from {
        opacity: 0;
        transform: translateY(-8px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    /* Ensure organization switcher styling */
    .cl-organizationSwitcherTrigger {
      width: 100% !important;
      padding: 8px 12px !important;
      background: var(--background) !important;
      border: 1px solid var(--border) !important;
      border-radius: 8px !important;
      transition: all 0.15s ease !important;
    }
    .cl-organizationSwitcherTrigger:hover {
      background: var(--muted) !important;
    }
  </style>
`;

export const SideNav: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    isOpenOnSmallScreens: isSidebarOpenOnSmallScreens,
    setOpenOnSmallScreens,
    isPageWithSidebar
  } = useSideNavContext();
  const [profile, setProfile] = useState<any>({});
  const { getUserProfile, updateUserProfile } = useResourcesService();


  const [mode, _, toggleMode] = useThemeMode();
  const notificationData = useSelector((state: any) => state.notification.data);
  const JWTdata: any = useJwt();

  const { org_id: orgId = "", org_role: userRole = "" } = JWTdata || {};
  const { setActive } = useOrganizationList();
  const showStrategyMaps = orgId !== ORG_HIDE_STRATEGY_MAPS;

  // Warning modal state for navigation away from Resources page
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Use shared hook for warning check
  const { shouldShowWarning, unallocatedTasksCount, totalStoryPoints } = useUnallocatedTasksWarning();

  // Saved view state from Redux
  const activeViewId = useSelector((state: any) => state.views.activeViewId) as number | null;
  const preViewFilters = useSelector((state: any) => state.views.preViewFilters) as Record<string, Record<string, string[]>>;
  const allViews = useSelector((state: any) => state.views.views);
  const activeView = activeViewId != null ? allViews?.find((v: any) => v.id === activeViewId) : null;
  const activeViewComponent = activeView?.component as string | undefined;

  // Determine if a saved view is active for each nav section
  const isViewActiveForWorkItems = activeViewId != null && activeViewComponent === ComponentPage.WORK_ITEMS;
  const isViewActiveForStrategyMaps = activeViewId != null && activeViewComponent === ComponentPage.STRATEGY_MAPS;
  const isViewActiveForPeople = activeViewId != null && activeViewComponent === ComponentPage.PEOPLE;

  const {
    currentEntity,
    currentEntityId
  } = useSelector((state: any) => state.timeline);

  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  // determine if sidebar is collapsed so we can conditionally render labels / section headers
  const isCollapsed = (isSmallScreen() ? !isSidebarOpenOnSmallScreens : isSidebarOpenOnSmallScreens);
  const sidebarBg = "bg-gradient-to-b from-blue-700 to-blue-900";

  // Refs
  
  const settingLinkRef = useRef<HTMLAnchorElement>(null);
  const strategyMapsLinkRef = useRef<HTMLAnchorElement>(null);
  const resourcesLinkRef = useRef<HTMLAnchorElement>(null);
  const projectsLinkRef = useRef<HTMLAnchorElement>(null);




    const loadProfile = async () => {
      if (orgId) {
        try {
          const data: any = await getUserProfile();
          setProfile(data);
        } catch (error) {
        } finally {
        }
      }
    };

    useEffect(() => {
      loadProfile();
    }, [orgId]);


  // Inject custom tooltip styles
  useEffect(() => {
    const styleElement = document.createElement('div');
    styleElement.innerHTML = sidebarTooltipStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(async (JWT) => {
      const { org_id = "", org_list = [] } = JWT
      if (!org_id) {
        if (Object.keys(org_list).length) {
          const [firstOrgId = ''] = Object.keys(org_list)
          if (firstOrgId && typeof setActive == "function") {
            await setActive({ organization: firstOrgId })
          } else {
            window.location.href = "/signup"
          }
        } else {
          window.location.href = "/signup"
        }
      }
    }, 4000, JWTdata);
    return () => {
      clearTimeout(t)
    }
  }, [JWTdata]);

  const read_len = notificationData.filter((noti: any) => !noti.read).length

  const PROJECT_WITH_ID_REGEX = /^\/?projects\/[0-9a-fA-F-]+(?:\/v)?\/?$/;
  const STRATEGY_MAP_WITH_ID_REGEX = /^\/?strategymaps\/[0-9a-fA-F-]+(?:\/v)?\/?$/;
  const ROADMAPS_WITH_ID_REGEX = /^\/?roadmaps\/[0-9a-fA-F-]+(?:\/v)?\/?$/;

  // Helper function to handle navigation with warning check
  const handleNavigationWithWarning = (path: string, e?: React.MouseEvent) => {
    // Only check warning if currently on Resources page and warning should be shown
    if (location.pathname.includes("/resources") && shouldShowWarning) {
      e?.preventDefault();
      e?.stopPropagation();
      setPendingNavigation(path);
      setShowWarningModal(true);
    }
    // If no warning needed, let the Link component handle navigation normally (don't preventDefault)
  };

  /**
   * When clicking a nav item while a saved view is active for that page,
   * clear the saved view and restore the pre-view filters from Redux.
   */
  const handleNavClickWithViewRestore = (targetPath: string, navComponents: string[], e?: React.MouseEvent) => {
    // Check if a saved view is active for this nav item's page
    if (activeViewId != null && activeViewComponent && navComponents.includes(activeViewComponent)) {
      e?.preventDefault();
      e?.stopPropagation();

      // Build URL from pre-view filters config stored in Redux
      const config = preViewFilters?.[activeViewComponent];
      const params = new URLSearchParams();
      if (config) {
        for (const [key, values] of Object.entries(config)) {
          const joined = values.join(',');
          params.set(key, joined); // Always set, even if empty (prevents stale localStorage fallback)
        }
      }

      dispatch(clearActiveView());
      const queryString = params.toString();
      const navigateTo = queryString ? `${targetPath}?${queryString}` : targetPath;
      navigate(navigateTo);
      return;
    }

    // Otherwise fall through to normal navigation / warning check
    handleNavigationWithWarning(targetPath, e);
  };

  let chatBoxProps: any = {};
  if (location.pathname.includes("strategymaps")) {
    if (STRATEGY_MAP_WITH_ID_REGEX.test(location.pathname)) {
      chatBoxProps = {
        project_id: currentEntity === ENTITY.PROJECT ? currentEntityId : null,
      };
    } else {
      chatBoxProps = {};
    }
  } else if (location.pathname.includes("roadmaps")) {
    if (ROADMAPS_WITH_ID_REGEX.test(location.pathname)) {
      chatBoxProps = {
        project_id: currentEntity === ENTITY.PROJECT ? currentEntityId : null,
      };
    } else {
      chatBoxProps = {};
    }
  } else if (location.pathname.includes("projects")) {
    if (PROJECT_WITH_ID_REGEX.test(location.pathname)) {
      chatBoxProps = {
        project_id: currentEntity === ENTITY.PROJECT ? currentEntityId : null,
      };
    } else {
      chatBoxProps = {};
    }
  }

  

  return (
    <>
      <div
        className={classNames("fixed left-0 top-6 z-40", {
          hidden: !isSmallScreen(),
        })}
      >
        <button
          onClick={() => setOpenOnSmallScreens(!isSidebarOpenOnSmallScreens)}
          className="mr-3 cursor-pointer rounded-lg p-2 text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out lg:inline bg-primary-dark"
        >
          <HiMenuAlt1 className="h-6 w-6 text-white" />
        </button>
      </div>

      {/* Responsive width & color wrapper */}
      <div
        className={`fixed lg:relative lg:!block transition-all duration-300 z-50 h-screen ${isCollapsed ? "w-[65px]" : "w-[255px]"} ${isSidebarOpenOnSmallScreens ? "" : "hidden lg:block"} bg-gradient-to-b from-blue-700 to-blue-900`}
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
      >
        {isSmallScreen() && isSidebarOpenOnSmallScreens && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setOpenOnSmallScreens(false)}
          />
        )}

        <Sidebar
          aria-label="Sidebar with multi-level dropdown"
          collapsed={
            isSmallScreen()
              ? !isSidebarOpenOnSmallScreens
              : isSidebarOpenOnSmallScreens
          }
          className={`sidebarinner !${sidebarBg} relative z-[9999]`}
          theme={{
            root: {
              inner: `h-full overflow-y-auto overflow-x-hidden ${sidebarBg} py-4 px-3`,
              // inner:`h-full overflow-y-auto overflow-x-hidden ${isCollapsed ? "bg-[#233876]" : "bg-[#1A2234]"} py-4 px-3`
            },
            item: {
              base: "flex items-center justify-center rounded-lg p-2 text-base font-normal text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 transition-all duration-300 ease-in-out",
              active:
                "bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg border-l-4 border-blue-400",
              content: {
                base: "px-3 flex-1 whitespace-nowrap text-sm font-medium",
              },
            },
          }}
        >
          <div className="flex flex-col h-[96vh]">
            {/* Header Section */}
            <div
              className={`flex items-center py-4 ${isCollapsed ? "px-0 justify-center" : "px-3"}`}
            >
              {isCollapsed ? (
                // Show logo and expand button when collapsed
                <div className="flex flex-col items-center gap-2">
                  <button
                    onClick={() =>
                      setOpenOnSmallScreens(!isSidebarOpenOnSmallScreens)
                    }
                    className="flex items-center group relative"
                  >
                    <img
                      src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
                      className={`h-9 w-9 transition-all duration-300 ease-in-out ${isSidebarHovered ? "opacity-0 scale-75 rotate-180" : "opacity-100 scale-100 rotate-0"}`}
                      alt="Cadencepro Logo"
                    />
                    <HiChevronDoubleRight
                      className={`h-9 w-9 text-white absolute inset-0 transition-all duration-300 ease-in-out p-2 ${isSidebarHovered ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-75 rotate-180"}`}
                    />
                  </button>
                </div>
              ) : (
                // Show full logo with text and collapse button when expanded
                <>
                  <Link
                    to={showStrategyMaps ? "/strategymaps" : "/"}
                    className="flex items-center flex-1"
                    onClick={(e) =>
                      handleNavigationWithWarning(
                        showStrategyMaps ? "/strategymaps" : "/",
                        e,
                      )
                    }
                  >
                    <img
                      src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
                      className="mr-1 h-5 sm:h-9 -ml-2.5"
                      alt="Cadencepro Logo"
                    />
                    <div className="text-center">
                      <span className="text-gray-200 text-xl font-semibold font-['Inter'] ml-1">
                        Cadencepro
                      </span>
                    </div>
                  </Link>
                  {isPageWithSidebar && (
                    <button
                      onClick={() =>
                        setOpenOnSmallScreens(!isSidebarOpenOnSmallScreens)
                      }
                      className="flex items-center group relative"
                    >
                      <HiChevronDoubleLeft
                        className={`h-9 w-9 text-white transition-opacity duration-200 p-2 ${isSidebarHovered ? "opacity-0" : "opacity-100"}`}
                      />
                      <HiChevronDoubleLeft
                        className={`h-9 w-9 text-white absolute inset-0 transition-opacity duration-200 p-2 ${isSidebarHovered ? "opacity-100" : "opacity-0"}`}
                      />
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Content Area - Takes up remaining space */}
            <div className="flex-1 flex flex-col">
              {/* PRIMARY NAVIGATION */}
              <Sidebar.ItemGroup className="!m-0 !p-0 !py-8 flex flex-col gap-1 !border-0">
                {/* Strategy Maps - hidden for certain orgs */}
                {showStrategyMaps && (
                  <Link
                    ref={strategyMapsLinkRef}
                    to={location.pathname.includes('/strategymaps') && !isViewActiveForStrategyMaps ? { pathname: '/strategymaps', search: location.search } : '/strategymaps'}
                    onClick={(e) =>
                      handleNavClickWithViewRestore("/strategymaps", [ComponentPage.STRATEGY_MAPS], e)
                    }
                  >
                    <Sidebar.Item
                      icon={HiOutlineViewBoards}
                      className={
                        location.pathname.includes("/strategymaps") && !isViewActiveForStrategyMaps
                          ? "!bg-white !rounded-lg !text-black shadow-lg border-l-4 border-blue-400 ring-2 ring-blue-400 ring-offset-0"
                          : "!rounded-lg text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out"
                      }
                      theme={{
                        icon: {
                          base: `text-2xl ${location.pathname.includes("/strategymaps") && !isViewActiveForStrategyMaps ? "text-black" : "text-white"}`,
                        },
                      }}
                    >
                      Strategy Maps
                    </Sidebar.Item>
                  </Link>
                )}

                {/* Work Items */}
                <Link
                  ref={projectsLinkRef}
                  to={location.pathname.includes('/workitems') ? { pathname: '/workitems', search: location.search } : '/workitems'}
                  onClick={(e) => handleNavClickWithViewRestore("/", [ComponentPage.WORK_ITEMS], e)}
                >
                  <Sidebar.Item
                    icon={HiOutlineBriefcase}
                    className={
                      (location.pathname.includes("/workitems") ||
                      location.pathname.includes("/projects") ||
                      location.pathname.includes("/roadmaps") ||
                      location.pathname === "/") && !isViewActiveForWorkItems
                        ? "!bg-white !rounded-lg !text-black shadow-lg border-l-4 border-blue-400 ring-2 ring-blue-400 ring-offset-0"
                        : "!rounded-lg text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out"
                    }
                    theme={{
                      icon: {
                        base: `text-2xl ${(location.pathname.includes("/workitems") || location.pathname.includes("/projects") || location.pathname.includes("/roadmaps") || location.pathname === "/") && !isViewActiveForWorkItems ? "text-black" : "text-white"}`,
                      },
                    }}
                  >
                    Work Items
                  </Sidebar.Item>
                </Link>

                {/* Resources */}
                <Link
                  ref={resourcesLinkRef}
                  to={location.pathname.includes('/resources') && !isViewActiveForPeople ? { pathname: '/resources', search: location.search } : '/resources'}
                  onClick={(e) => {
                    dispatch(resetResourcePageState());
                    handleNavClickWithViewRestore("/resources", [ComponentPage.PEOPLE], e);
                  }}
                >
                  <Sidebar.Item
                    icon={HiUsers}
                    className={
                      location.pathname.includes("/resources") && !isViewActiveForPeople
                        ? "!bg-white !rounded-lg !text-black shadow-lg border-l-4 border-blue-400 ring-2 ring-blue-400 ring-offset-0"
                        : "!rounded-lg text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out"
                    }
                    theme={{
                      icon: {
                        base: `text-2xl ${location.pathname.includes("/resources") && !isViewActiveForPeople ? "text-black" : "text-white"}`,
                      },
                    }}
                  >
                    People
                  </Sidebar.Item>
                </Link>
              </Sidebar.ItemGroup>

              {/* OTHERS SECTION */}
              <div className="text-white ">
                {/* Section header (hidden when collapsed) */}
                {!isCollapsed && (
                  <h3 className="px-3 mb-2 text-xs font-semibold pt-4 border-t border-slate-400/40  tracking-wider text-white uppercase">
                    Others
                  </h3>
                )}
                {isCollapsed && (
                  <div className="border-t border-slate-400/40 "></div>
                )}
                {/* Add consistent spacing for collapsed state */}
                {isCollapsed && <div className="pt-10 "></div>}

                <Sidebar.ItemGroup className="!m-0 !p-0 flex flex-col gap-1 !border-0">
                  {/* Activity */}
                  <div className="relative">
                    {read_len ? (
                      <span
                        className="w-4 h-4 text-center text-white pt-0.5 text-[8px] absolute right-0 bg-red-800 shadow-lg rounded-full z-40"
                        style={{
                          top: 12,
                          right: isSidebarOpenOnSmallScreens ? 5 : 197,
                        }}
                      >
                        {read_len}
                      </span>
                    ) : (
                      ""
                    )}
                  </div>

                  <Link
                    to="/notification"
                    onClick={(e) =>
                      handleNavigationWithWarning("/notification", e)
                    }
                  >
                    <Sidebar.Item
                      icon={HiOutlineBell}
                      className={
                        location.pathname.includes("/notification")
                          ? "!bg-white !rounded-lg !text-black shadow-lg border-l-4 border-blue-400 ring-2 ring-blue-400 ring-offset-0"
                          : "!rounded-lg text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out"
                      }
                      theme={{
                        icon: {
                          base: `text-2xl ${location.pathname.includes("/notification") ? "text-black" : "text-white"}`,
                        },
                      }}
                    >
                      My Activity
                    </Sidebar.Item>
                  </Link>

                  <Link
                    ref={settingLinkRef}
                    to="/settings"
                    onClick={(e) => handleNavigationWithWarning("/settings", e)}
                  >
                    <Sidebar.Item
                      icon={HiCog}
                      className={
                        location.pathname.includes("/settings")
                          ? "!bg-white !rounded-lg !text-black shadow-lg border-l-4 border-blue-400 ring-2 ring-blue-400 ring-offset-0"
                          : "!rounded-lg !text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out"
                      }
                      theme={{
                        icon: {
                          base: `text-2xl ${location.pathname.includes("/settings") ? "text-black" : "text-white"} !rounded-lg`,
                        },
                        content: {
                          base: "px-3 py-2 text-left",
                        },
                      }}
                    >
                      Settings
                    </Sidebar.Item>
                  </Link>

                  <Link to="#">
                    <Sidebar.Item
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        toggleMode();
                        let newMode = mode === "dark" ? "light" : "dark";
                        localStorage.setItem("theme", newMode);
                      }}
                      icon={() => (
                        <div
                          className="text-2xl !outline-none !ring-0 !rounded-lg w-6 h-6 flex items-center justify-center transition-colors duration-200"
                          style={{
                            color: "white",
                          }}
                          onMouseEnter={(e) => {
                            if (mode === "light") {
                              (e.currentTarget as HTMLElement).style.color =
                                "#000000";
                            }
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.color =
                              "white";
                          }}
                        >
                          {mode === "dark" ? <HiSun /> : <HiMoon />}
                        </div>
                      )}
                      className="!rounded-lg !text-white hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out"
                      theme={{
                        base: "!outline-none !ring-0 focus:!outline-none focus:!ring-0 active:!ring-0 flex items-center justify-center rounded-lg p-2 text-base font-normal text-white",
                        content: {
                          base: "px-3 flex-1 whitespace-nowrap text-sm font-medium",
                        },
                      }}
                    >
                      {mode === "dark" ? "Light Mode" : "Dark Mode"}
                    </Sidebar.Item>
                  </Link>
                </Sidebar.ItemGroup>
              </div>

              {/* SAVED VIEWS SECTION */}
              <SavedViews isCollapsed={isCollapsed} />

              {/* User Profile Section - Positioned at bottom */}
              <div
                className={`mt-auto pt-4 pb-4 ${isCollapsed ? "flex justify-center" : ""}`}
              >
                <UserDropdown
                  setActive={setActive}
                  JWTdata={JWTdata}
                  isCollapsed={isCollapsed}
                  profile={profile}
                />
              </div>
            </div>
          </div>
        </Sidebar>
      </div>

      <SideNavGuide
        refs={{
          settingLinkRef,
          strategyMapsLinkRef,
          resourcesLinkRef,
          projectsLinkRef,
        }}
      />
      <Portal>
        <>
          {(location.pathname === "/" ||
            location.pathname.includes("/workitems") ||
            location.pathname.includes("/projects") ||
            location.pathname.includes("/strategymaps") ||
            location.pathname.includes("/roadmaps") ||
            location.pathname.includes("/resources")) && (
            <GlobalChatBox {...chatBoxProps} />
          )}
        </>
      </Portal>

      {/* Unallocated Tasks Warning Modal for Navigation */}
      <UnallocatedTasksWarningModal
        showModal={showWarningModal}
        unallocatedTasksCount={unallocatedTasksCount}
        totalStoryPoints={totalStoryPoints}
        setShowModal={setShowWarningModal}
        onConfirm={() => {
          if (pendingNavigation) {
            navigate(pendingNavigation);
            setPendingNavigation(null);
          }
          setShowWarningModal(false);
        }}
        onCancel={() => {
          setPendingNavigation(null);
          setShowWarningModal(false);
        }}
      />
    </>
  );
};

const UserDropdown: FC<any> = function ({ setActive, JWTdata, isCollapsed, profile }: any) {
  const { signOut } = useClerk()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleLogoutClick = () => {
    localStorage.removeItem("clerk-db-jwt");
    localStorage.removeItem("workingDays");
    signOut();
    window.location.href = "/";
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orgId = urlParams.get('orgid');
    const { org_list } = JWTdata
    if (orgId) {
      setTimeout(() => {
        if (org_list && org_list[orgId]) {
          setActive({ organization: orgId })
        }
        setActive({ organization: orgId })
      }, 5000)
    }
  }, [])


  return (
      <WithUser>
        {(user) => {

          const full_name = profile?.first_name ? profile?.first_name + " " + profile?.last_name : user?.fullName || user.username;
          const email = profile?.email || user?.primaryEmailAddress?.emailAddress;
          const image_url = profile?.image_url || (user as any).imageUrl;
        return (
          <div className={`${isCollapsed ? "flex justify-center" : ""}`}>
            {!isCollapsed ? (
              <FloatingDropdown
                isOpen={isDropdownOpen}
                onToggle={toggleDropdown}
                onClose={closeDropdown}
                trigger={<button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isDropdownOpen}
                  className="flex items-center space-x-3 px-2 -ml-1 border-t border-slate-800/70  text-white w-full text-left cursor-pointer"
                >
                  <Avatar
                    alt=""
                    img={image_url}
                    className="rounded-md border-2 border-white/20 shadow-xl mt-5"
                    size="sm" />
                  <div className="flex-1 mt-5 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {full_name}
                    </div>
                    <div className="text-xs text-white truncate">
                      {email}
                    </div>
                  </div>
                  <div className="text-white mt-5 p-2 rounded-lg hover:bg-gradient-to-r hover:from-slate-600 hover:to-slate-700 hover:shadow-md transition-all duration-300 ease-in-out">
                    <HiCog className="h-4 w-4" />
                  </div>
                </button>}
              >
                {/* Modern User Card */}
                <div className="relative">
                  {/* Header with gradient */}
                  <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className="rounded-md  relative">
                        <Avatar
                          alt=""
                          img={image_url}
                          className="rounded-md border-2 border-white/20 shadow-xl"
                          size="md" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-white truncate">
                          {full_name}
                        </h3>
                        <p className="text-xs text-blue-100 truncate">
                          {email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="bg-gradient-to-b from-blue-700 to-blue-900">
                    {/* Organization Section */}
                    <div className="px-4 py-3 border-b border-slate-600">
                      <div className="flex items-center space-x-2 mb-2"></div>
                      <div className="ml-8">
                        <OrganizationSwitcher
                          hidePersonal
                          afterSwitchOrganizationUrl="/"
                          appearance={{
                            elements: {
                              organizationSwitcherPopoverActionButton__manageOrganization: "hidden",
                              organizationSwitcherPopoverActionButtonIconBox__manageOrganization: "hidden",
                              organizationSwitcherPopoverActionButtonIcon__manageOrganization: "hidden",
                              organizationSwitcherPopoverActionButtonText__manageOrganization: "hidden",
                              organizationSwitcherTrigger: "w-full p-1.5 bg-slate-600 hover:bg-slate-500 rounded-md border border-slate-500 transition-colors text-xs text-white",
                              organizationSwitcherTriggerIcon: "text-white",
                            },
                          }} />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="px-4 py-3">
                      <button
                        onClick={() => {
                          handleLogoutClick();
                          closeDropdown();
                        } }
                        className="w-full flex items-center space-x-2 px-3 py-2 text-xs font-medium text-red-200 bg-red-800/20 hover:bg-red-800/30 rounded-md transition-all duration-200 group"
                      >
                        <div className="w-6 h-6 bg-red-800/30 rounded-md flex items-center justify-center group-hover:bg-red-800/50 transition-colors">
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="font-medium">Sign out</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </FloatingDropdown>
            ) : (
              // Collapsed view - just avatar with dropdown
              <div className="border-t border-slate-800/70  pt-5">
                <FloatingDropdown
                  isOpen={isDropdownOpen}
                  onToggle={toggleDropdown}
                  onClose={closeDropdown}
                  trigger={<span>
                    <span className="sr-only">User menu</span>
                    <Avatar
                      alt=""
                      img={(user as any).imageUrl}
                      className="rounded-md border-2 border-white/20 shadow-xl mb-0.5 hover:ring-2 hover:ring-blue-400 hover:ring-opacity-50 transition-all duration-200 cursor-pointer"
                      size="sm" />
                  </span>}
                >
                  {/* Modern User Card */}
                  <div className="relative">
                    {/* Header with gradient */}
                    <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div className="rounded-md  relative">
                          <Avatar
                            alt=""
                            img={image_url}
                            className="rounded-md border-2 border-white/20 shadow-xl"
                            size="md" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-white truncate">
                            {full_name}
                          </h3>
                          <p className="text-xs text-blue-100 truncate">
                            {email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="bg-gradient-to-b from-blue-700 to-blue-900">
                      {/* Organization Section */}
                      <div className="px-4 py-3 border-b border-slate-600">
                        <div className="w-fit" style={{ margin: "0 auto" }}>
                          <OrganizationSwitcher
                            hidePersonal
                            afterSwitchOrganizationUrl="/"
                            appearance={{
                              elements: {
                                organizationSwitcherPopoverActionButton__manageOrganization: "hidden",
                                organizationSwitcherPopoverActionButtonIconBox__manageOrganization: "hidden",
                                organizationSwitcherPopoverActionButtonIcon__manageOrganization: "hidden",
                                organizationSwitcherPopoverActionButtonText__manageOrganization: "hidden",
                                organizationSwitcherTrigger: "w-full p-1.5 bg-slate-600 hover:bg-slate-500 rounded-md border border-slate-500 transition-colors text-xs text-white",
                                organizationSwitcherTriggerIcon: "text-white",
                              },
                            }} />
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="px-4 py-3">
                        <button
                          onClick={() => {
                            handleLogoutClick();
                            closeDropdown();
                          } }
                          className="w-full flex items-center space-x-2 px-3 py-2 text-xs font-medium text-red-200 bg-red-800/20 hover:bg-red-800/30 rounded-md transition-all duration-200 group"
                        >
                          <div className="w-6 h-6 bg-red-800/30 rounded-md flex items-center justify-center group-hover:bg-red-800/50 transition-colors">
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                          </div>
                          <div className="text-left">
                            <div className="font-medium">Sign out</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </FloatingDropdown>
              </div>
            )}
          </div>
        );
      }}
      </WithUser>

  );
};
